import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { createOrder, getProducts, getWarehouses } from '../config/api';
import { getMockProducts } from '../data/mockData';
import useIsMobile from '../hooks/useIsMobile';

const CreateOrder = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(true);
  const [orderData, setOrderData] = useState({
    customerName: '',
    customerEmail: '',
    phone: '',
    source: 'Online',
    warehouseId: '',
    selectedWarehouse: null,
    targetHubId: '',
    selectedTargetHub: null,
    gstNumber: '',
    companyName: '',
    paymentMode: 'Online',
    deliveryDay: 'Delivery',
    shippingAddress: '',
    city: '',
    state: '',
    pincode: '',
    status: 'pending',
    confirmed: false,
    discount: 0,
    transportationCharges: 0,
    handlingCharges: 0,
    items: [
      { productId: '', name: '', quantity: '', price: '', selectedOption: null }
    ]
  });
  
  const [gstError, setGstError] = useState('');

  useEffect(() => {
    loadProducts();
    loadWarehouses();
  }, []);

  const loadProducts = async () => {
    try {
      const productListRes = await getProducts();
      const productList = productListRes.data || [];
      setProducts(productList);
      const options = productList.map(product => ({
        value: product.id,
        label: `${product.skuCode} - ${product.name}`,
        product: product,
        skuCode: product.skuCode
      }));
      setProductOptions(options);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses();
      if (response && response.data) {
        setWarehouses(response.data);
        const options = response.data.map(warehouse => ({
          value: warehouse.id,
          label: `${warehouse.name} - ${warehouse.city}, ${warehouse.state}`,
          warehouse: warehouse
        }));
        setWarehouseOptions(options);
      }
    } catch (error) {
      console.error('Error loading warehouses:', error);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Validate GST number format
    if (name === 'gstNumber') {
      validateGSTNumber(value);
    }
    
    setOrderData({
      ...orderData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleWarehouseChange = (selectedOption) => {
    setOrderData({
      ...orderData,
      warehouseId: selectedOption ? selectedOption.value : '',
      selectedWarehouse: selectedOption
    });
  };

  const handleTargetHubChange = (selectedOption) => {
    setOrderData({
      ...orderData,
      targetHubId: selectedOption ? selectedOption.value : '',
      selectedTargetHub: selectedOption
    });
  };

  const validateGSTNumber = (gstNumber) => {
    if (!gstNumber) {
      setGstError('');
      return true;
    }
    
    // GST format: 2 digits (state code) + 10 alphanumeric (PAN) + 1 digit (entity number) + 1 letter (Z) + 1 alphanumeric (checksum)
    // Example: 22AAAAA0000A1Z5
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    
    if (!gstRegex.test(gstNumber)) {
      setGstError('Invalid GST format. Format: 22AAAAA0000A1Z5');
      return false;
    }
    
    setGstError('');
    return true;
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...orderData.items];
    
    if (field === 'productId') {
      // Handle react-select change
      if (value && value.product) {
        const selectedProduct = value.product;
        updatedItems[index] = {
          ...updatedItems[index],
          productId: selectedProduct.id,
          name: selectedProduct.name,
          selectedOption: value,
          skuCode: selectedProduct.skuCode
        };
      } else {
        // Clear selection
        updatedItems[index] = {
          ...updatedItems[index],
          productId: '',
          name: '',
          price: 0,
          selectedOption: null
        };
      }
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value
      };
    }
    
    setOrderData({
      ...orderData,
      items: updatedItems
    });
  };

  const addItem = () => {
    setOrderData({
      ...orderData,
      items: [...orderData.items, { productId: '', name: '', quantity: 1, price: 0, selectedOption: null }]
    });
  };

  const removeItem = (index) => {
    if (orderData.items.length > 1) {
      const updatedItems = orderData.items.filter((_, i) => i !== index);
      setOrderData({
        ...orderData,
        items: updatedItems
      });
    }
  };

  const calculateSubtotal = () => {
    return orderData.items.reduce((total, item) => {
      const gstPercent = item.selectedOption?.product?.gst_percent || 5;
      const unitPriceWithGST = item.price * (1 + gstPercent / 100);
      return total + (item.quantity * unitPriceWithGST);
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = parseFloat(orderData.discount) || 0;
    const transportation = parseFloat(orderData.transportationCharges) || 0;
    const handling = parseFloat(orderData.handlingCharges) || 0;
    
    return subtotal - discount + transportation + handling;
  };

  // Custom styles for react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '42px',
      border: state.isFocused ? '2px solid #0d6efd' : '1px solid #ced4da',
      borderRadius: '6px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#0d6efd' : '#ced4da'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? '#0d6efd' 
        : state.isFocused 
        ? '#f8f9fa' 
        : 'white',
      color: state.isSelected ? 'white' : '#212529',
      '&:hover': {
        backgroundColor: state.isSelected ? '#0d6efd' : '#f8f9fa'
      }
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: '9999 !important'
    }),
    menuPortal: base => ({
      ...base,
      zIndex: 9999
    })
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate warehouse selection
    if (!orderData.warehouseId) {
      alert('Please select a warehouse');
      return;
    }

    // Hub-to-hub orders require a target hub (it may be the same as the source).
    if (orderData.source === 'hub to hub') {
      if (!orderData.targetHubId) {
        alert('Please select a target hub for the hub-to-hub order');
        return;
      }
    }
    
    // Validate GST number if provided
    if (orderData.gstNumber && !validateGSTNumber(orderData.gstNumber)) {
      alert('Please enter a valid GST number');
      return;
    }
    
    setLoading(true);

    try {
      // Generate order ID
      const orderId = `ORD-${Date.now()}`;
      
      // Calculate amounts
      const subtotal = calculateSubtotal();
      const totalAmount = calculateTotal();
      
      // Prepare order data (remove UI-only fields: selectedWarehouse/selectedTargetHub)
      const { selectedWarehouse, selectedTargetHub, ...orderDataToSend } = orderData;
      
      const newOrder = {
        id: orderId,
        ...orderDataToSend,
        subtotal,
        totalAmount,
        orderDate: new Date().toISOString().split('T')[0]
      };

      console.log(newOrder);

      // Call API to create the order
      let response = await createOrder(newOrder) || {};
      
      response = response.data || {};

      if(response.error) {
        alert(response.error || 'Some error occured');
        return;
      }

      alert(`Order ${orderId} created successfully!`);
      
      // Close the tab and refresh parent window
      if (window.opener) {
        window.opener.location.reload();
        window.close();
      } else {
        navigate('/orders');
      }

    } catch (error) {
      console.error('Error creating order:', error);
      alert('Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div className="order-detail-header">
          <div className="order-detail-title">
            <div className="breadcrumb">
              <Link to="/orders">Orders</Link> / Create New Order
            </div>
            <h1>Create New Order</h1>
          </div>
          
          <div className="order-actions">
            <button
              type="button"
              onClick={() => (window.opener ? window.close() : navigate('/orders'))}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="create-order-form"
              disabled={loading}
              className="btn btn-success"
            >
              {loading ? 'Creating...' : 'Create Order'}
            </button>
          </div>
        </div>

        <form id="create-order-form" onSubmit={handleSubmit}>
          {/* Customer Information */}
          <div className="card">
            <h3 className="section-title">Customer Information</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Customer Name *</label>
                <input
                  type="text"
                  name="customerName"
                  className="form-control"
                  value={orderData.customerName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter customer name"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Customer Email *</label>
                <input
                  type="email"
                  name="customerEmail"
                  className="form-control"
                  value={orderData.customerEmail}
                  onChange={handleInputChange}
                  required
                  autoComplete="email"
                  placeholder="Enter customer email"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  value={orderData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter phone number"
                  maxLength="10"
                  pattern="[0-9]{10}"
                  inputMode="tel"
                  autoComplete="tel"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Warehouse *</label>
                <Select
                  value={orderData.selectedWarehouse}
                  onChange={handleWarehouseChange}
                  options={warehouseOptions}
                  styles={selectStyles}
                  placeholder={loadingWarehouses ? 'Loading warehouses...' : 'Select warehouse...'}
                  isSearchable={true}
                  isClearable={true}
                  isLoading={loadingWarehouses}
                  isDisabled={loadingWarehouses}
                  noOptionsMessage={() => 'No warehouses found'}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  required
                />
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Select the warehouse for order fulfillment
                </small>
              </div>
              
              <div className="info-item">
                <label className="info-label">Source *</label>
                <select
                  name="source"
                  className="form-control"
                  value={orderData.source}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Online">Online</option>
                  <option value="Channel">Channel</option>
                  <option value="ZS B2C">ZS B2C</option>
                  <option value="ZS B2B">ZS B2B</option>
                  <option value="hub to hub">hub to hub</option>
                  <option value="damaged">damaged</option>
                </select>
              </div>

              {orderData.source === 'hub to hub' && (
                <div className="info-item">
                  <label className="info-label">Target Hub *</label>
                  <Select
                    value={orderData.selectedTargetHub}
                    onChange={handleTargetHubChange}
                    options={warehouseOptions}
                    styles={selectStyles}
                    placeholder={loadingWarehouses ? 'Loading hubs...' : 'Select target hub...'}
                    isSearchable={true}
                    isClearable={true}
                    isLoading={loadingWarehouses}
                    isDisabled={loadingWarehouses}
                    noOptionsMessage={() => 'No hubs found'}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                  />
                  <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Destination hub for this stock transfer (must differ from the source warehouse)
                  </small>
                </div>
              )}
              
              <div className="info-item">
                <label className="info-label">Payment Mode *</label>
                <select
                  name="paymentMode"
                  className="form-control"
                  value={orderData.paymentMode}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Online">Online</option>
                  <option value="Wallet">Wallet</option>
                </select>
              </div>

              <div className="info-item">
                <label className="info-label">Order Type *</label>
                <select
                  name="deliveryDay"
                  className="form-control"
                  value={orderData.deliveryDay}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Delivery">Delivery</option>
                  <option value="self">Self Pickup</option>
                </select>
              </div>
              
              <div className="info-item">
                <label className="info-label">Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  className="form-control"
                  value={orderData.companyName}
                  onChange={handleInputChange}
                  placeholder="Enter company name (optional)"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">GST Number</label>
                <input
                  type="text"
                  name="gstNumber"
                  className="form-control"
                  value={orderData.gstNumber}
                  onChange={handleInputChange}
                  placeholder="22AAAAA0000A1Z5"
                  maxLength="15"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{ 
                    borderColor: gstError ? '#dc3545' : '#ced4da',
                    textTransform: 'uppercase'
                  }}
                />
                {gstError && (
                  <small style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {gstError}
                  </small>
                )}
              </div>
              
              
            </div>
          </div>

          {/* Shipping Address */}
          <div className="card">
            <h3 className="section-title">Shipping Address</h3>
            <div className="order-info">
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <label className="info-label">Street Address *</label>
                <textarea
                  name="shippingAddress"
                  className="form-control"
                  value={orderData.shippingAddress}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  placeholder="Enter street address, apartment, suite, etc."
                  style={{ resize: 'vertical' }}
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">City *</label>
                <input
                  type="text"
                  name="city"
                  className="form-control"
                  value={orderData.city}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter city"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">State *</label>
                <input
                  type="text"
                  name="state"
                  className="form-control"
                  value={orderData.state}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter state"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Pincode *</label>
                <input
                  type="text"
                  name="pincode"
                  className="form-control"
                  value={orderData.pincode}
                  onChange={handleInputChange}
                  required
                  pattern="[0-9]{6}"
                  maxLength="6"
                  inputMode="numeric"
                  placeholder="Enter 6-digit pincode"
                />
              </div>
            </div>
            
            {/* Address Preview */}
            {/* <div className="address-card" style={{ marginTop: '20px' }}>
              <h4 style={{ marginBottom: '10px', color: '#495057', fontSize: '14px', fontWeight: '600' }}>
                Address Preview:
              </h4>
              <p style={{ margin: '0', color: '#6c757d', fontStyle: 'italic' }}>
                {orderData.shippingAddress || 'Street address will appear here'}
                {(orderData.city || orderData.state || orderData.pincode) && (
                  <>
                    <br />
                    {[orderData.city, orderData.state, orderData.pincode].filter(Boolean).join(', ')}
                  </>
                )}
              </p>
            </div> */}
          </div>

          {/* Order Items */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 className="section-title" style={{ marginBottom: '0' }}>Order Items</h3>
              <button
                type="button"
                onClick={addItem}
                className="btn btn-primary"
                style={{ fontSize: '12px', padding: '8px 16px' }}
              >
                + Add Item
              </button>
            </div>
            
            <div className="order-items-table">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Unit Price (Excl. GST)</th>
                    <th>GST %</th>
                    <th>Unit Price (Incl. GST)</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orderData.items.map((item, index) => {
                    const gstPercent = item.selectedOption?.product?.gst_percent || 0;
                    const unitPriceWithGST = item.price * (1 + gstPercent / 100);
                    
                    return (
                      <tr key={index}>
                        <td>
                          <Select
                            value={item.selectedOption}
                            onChange={(selectedOption) => handleItemChange(index, 'productId', selectedOption)}
                            options={productOptions}
                            styles={selectStyles}
                            placeholder={loadingProducts ? 'Loading products...' : 'Search and select a product...'}
                            isSearchable={true}
                            isClearable={true}
                            isLoading={loadingProducts}
                            isDisabled={loadingProducts}
                            noOptionsMessage={() => 'No products found'}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="form-control"
                            min="1"
                            required
                            style={{ width: '80px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={item.price}
                            onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                            className="form-control"
                            min="1"
                            step="0.01"
                            required
                            style={{ width: '120px' }}
                            placeholder={item.productId ? 'Enter price' : 'Enter price'}
                          />
                        </td>
                        <td style={{ textAlign: 'center', color: '#6c757d', fontWeight: '500' }}>
                          {gstPercent}%
                        </td>
                        <td style={{ textAlign: 'center', color: '#0d6efd', fontWeight: '600' }}>
                          {unitPriceWithGST.toFixed(2)}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', color: '#0d6efd' }}>
                          {(item.quantity * unitPriceWithGST).toFixed(2)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {orderData.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="btn btn-warning"
                              style={{ fontSize: '12px', padding: '4px 8px' }}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Order Summary */}
          <div className="card">
            <h3 className="section-title">Order Summary</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Subtotal</label>
                <div className="info-value" style={{ fontSize: '18px', color: '#6c757d' }}>
                  ₹{calculateSubtotal().toFixed(2)}
                </div>
              </div>
              
              <div className="info-item">
                <label className="info-label">Discount</label>
                <input
                  type="number"
                  name="discount"
                  className="form-control"
                  value={orderData.discount}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="Enter discount amount"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Transportation Charges</label>
                <input
                  type="number"
                  name="transportationCharges"
                  className="form-control"
                  value={orderData.transportationCharges}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="Enter transportation charges"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Handling Charges</label>
                <input
                  type="number"
                  name="handlingCharges"
                  className="form-control"
                  value={orderData.handlingCharges}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="Enter handling charges"
                />
              </div>
              
              <div className="info-item">
                <label className="info-label">Total Amount</label>
                <div className="info-value" style={{ fontSize: '24px', color: '#0d6efd', fontWeight: '700' }}>
                  ₹{calculateTotal().toFixed(2)}
                </div>
              </div>
            </div>
          </div>
          {isMobile && (
            <div className="m-sticky-actions">
              <button
                type="button"
                onClick={() => (window.opener ? window.close() : navigate('/orders'))}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn btn-success"
              >
                {loading ? 'Creating...' : 'Create Order'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateOrder;